// @figma file=tbd node=tbd name="ChatPanel" set="code-factory" verified=tbd

import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── ChatPanel ────────────────────────────────────────────────────────────────

export interface ChatPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Top section — use ChatPanelHeader or pass custom JSX */
  header?: React.ReactNode;
  /** Bottom section — typically a ChatInput */
  footer?: React.ReactNode;
  /** Middle body — typically a ChatConversation */
  children?: React.ReactNode;
}

const ChatPanel = React.forwardRef<HTMLDivElement, ChatPanelProps>(
  ({ header, footer, children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Mobile: edge-to-edge, no decoration
        // sm+: card with border, radius, shadow
        'flex flex-col overflow-hidden bg-card',
        'sm:rounded-2xl sm:border sm:border-border sm:shadow-lg',
        className,
      )}
      {...props}
    >
      {header && <div className="shrink-0 border-b border-border">{header}</div>}
      <div className="flex flex-1 flex-col min-h-0">{children}</div>
      {footer && (
        <div className="shrink-0 border-t border-border p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {footer}
        </div>
      )}
    </div>
  ),
);
ChatPanel.displayName = 'ChatPanel';

// ─── ChatPanelHeader ──────────────────────────────────────────────────────────

export interface ChatPanelHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Avatar slot */
  avatar?: React.ReactNode;
  /** Agent or session name */
  name: string;
  /** Secondary line of text (role, status description, etc.) */
  subtitle?: string;
  /** Whether to show an online indicator dot */
  online?: boolean;
  /** Right-side action buttons */
  actions?: React.ReactNode;
}

const ChatPanelHeader = React.forwardRef<HTMLDivElement, ChatPanelHeaderProps>(
  ({ avatar, name, subtitle, online = false, actions, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-between gap-3 px-4 py-3', className)}
      {...props}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {avatar && <div className="shrink-0">{avatar}</div>}
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-none truncate">{name}</p>
          {(subtitle || online) && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground truncate">
              {online && (
                <span className="size-1.5 shrink-0 rounded-full bg-success inline-block" />
              )}
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
    </div>
  ),
);
ChatPanelHeader.displayName = 'ChatPanelHeader';

export { ChatPanel, ChatPanelHeader };
